import type { Probe, ProxyScelta } from '../api/types'
import { t } from '../i18n/lingua'

/**
 * La scelta del proxy per le chiamate che partono da una sonda: nessuno, uno
 * indicato qui, o quello configurato sulla sonda.
 *
 * Una sola componente per il connettore ITSM e per le credenziali delle
 * sorgenti in tempo reale (Wazuh, firewall, DNS, Veeam, ntopng): due moduli
 * scritti a parte divergerebbero alla prima modifica.
 */

export interface ProxyInput {
  mode: 'none' | 'custom' | 'probe'
  url: string
  username: string
  password: string
  togliPassword: boolean
}

export function proxyIniziale(p?: ProxyScelta | null): ProxyInput {
  return {
    mode: p?.mode ?? 'none',
    url: p?.url ?? '',
    username: p?.username ?? '',
    password: '',
    togliPassword: false,
  }
}

/** Ciò che si manda al Control Plane: la password solo se cambiata. */
export function proxyPayload(v: ProxyInput): Record<string, string> {
  if (v.mode !== 'custom') return { mode: v.mode }
  return {
    mode: 'custom',
    url: v.url.trim(),
    username: v.username.trim(),
    ...(v.password ? { password: v.password } : v.togliPassword ? { password: '' } : {}),
  }
}

export function SceltaProxy({
  value,
  onChange,
  salvata,
  sonda,
  senzaSonda,
}: {
  value: ProxyInput
  onChange: (v: ProxyInput) => void
  /** Se una password del proxy è già salvata (non torna mai indietro). */
  salvata?: boolean
  /** La sonda scelta: per dire subito se ha un proxy da usare. */
  sonda?: Probe
  /** La chiamata parte dal Control Plane: «proxy della sonda» non ha senso. */
  senzaSonda?: boolean
}) {
  const set = (patch: Partial<ProxyInput>) => onChange({ ...value, ...patch })
  const sondaSenzaProxy = value.mode === 'probe' && !senzaSonda && sonda && !sonda.proxy?.url
  return (
    <>
      <label className="field">
        <span>{t('Proxy')}</span>
        <select value={value.mode} onChange={(e) => set({ mode: e.target.value as ProxyInput['mode'] })}>
          <option value="none">{t('Nessun proxy')}</option>
          <option value="custom">{t('Proxy specificato')}</option>
          {!senzaSonda && <option value="probe">{t('Proxy della sonda')}</option>}
        </select>
        <span className="field__hint">
          {value.mode === 'probe'
            ? sonda?.proxy?.url
              ? t('Userà {url}, configurato nella scheda della sonda.', { url: sonda.proxy.url })
              : t('Il proxy si configura nella scheda della sonda (sezione Proxy).')
            : senzaSonda
              ? t('Per le chiamate verso questo sistema, fatte dal Control Plane.')
              : t('Applicato alle connessioni originate dalla sonda verso questo sistema.')}
        </span>
      </label>
      {sondaSenzaProxy && (
        <p className="banner banner--warn">
          {t('La sonda scelta non ha un proxy configurato: le chiamate falliranno finché non lo imposti nella sua scheda.')}
        </p>
      )}
      {value.mode === 'custom' && (
        <div className="row">
          <label className="field">
            <span>{t('Indirizzo del proxy')}</span>
            <input
              value={value.url}
              placeholder="http://proxy.azienda.local:3128"
              onChange={(e) => set({ url: e.target.value })}
              required
            />
          </label>
          <label className="field">
            <span>{t('Utente (facoltativo)')}</span>
            <input value={value.username} autoComplete="off" onChange={(e) => set({ username: e.target.value })} />
          </label>
          <label className="field">
            <span>{t('Password')}</span>
            <input
              type="password"
              value={value.password}
              autoComplete="new-password"
              placeholder={salvata ? t('salvata — lascia vuoto per non cambiarla') : ''}
              onChange={(e) => set({ password: e.target.value })}
            />
            {salvata && (
              <span className="check">
                <input
                  type="checkbox"
                  checked={value.togliPassword}
                  onChange={(e) => set({ togliPassword: e.target.checked })}
                />{' '}
                {t('Togli la password salvata')}
              </span>
            )}
          </label>
        </div>
      )}
    </>
  )
}
